import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import { get, has } from 'lodash-es';
import classnames from 'classnames';
import { ContentSection } from 'site-modules/shared/components/content-section/content-section';
import { GetPriceDropAlerts } from 'site-modules/inventory/components/get-price-drop-alerts/get-price-drop-alerts';
import { InventoryPriceChange } from 'site-modules/inventory/components/inventory-price-change/inventory-price-change';
import { InventoryHeart } from 'site-modules/shared/components/inventory-heart/inventory-heart';
import { InventoryEntities } from 'client/data/models/inventory';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { EventToolbox } from 'client/utils/event-toolbox';
import { HISTORY_PRICES_CONCEALED_STATE } from 'client/site-modules/shared/constants/inventory/prices';
import { getDifferenceInDays } from 'site-modules/shared/utils/date-utils';
import { isUsedDealCheckAvailable } from 'site-modules/shared/utils/car-buying/is-used-deal-check-available';
import { PriceCheckerChartVdp } from 'site-modules/shared/components/price-checker/price-checker-chart/price-checker-chart-vdp';
import { TooltipItem } from 'site-modules/shared/components/tooltip-item/tooltip-item';
import { TrackingConstant } from 'client/tracking/constant';
import { PriceHistoryGraph } from 'client/site-modules/shared/components/inventory/price-history-graph/price-history-graph';
import { DEAL_TYPE_ALLOWED_VALUES } from 'client/site-modules/shared/components/deal/deal-constants';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { getPlural } from 'client/utils/plural';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';

import './negotiation.scss';

/**
 * Shows section with price dynamic.
 * @param {object} vinData
 * @returns {ReactElement}
 */
export class Negotiation extends Component {
  static propTypes = {
    vinData: InventoryEntities.InventoryVin,
    isMobile: PropTypes.bool,
    isUsedNonPartnerDealCheckAvailable: PropTypes.bool,
    className: PropTypes.string,
  };

  static defaultProps = {
    vinData: {},
    isMobile: false,
    isUsedNonPartnerDealCheckAvailable: false,
    className: '',
  };

  onTooltipOpen = () => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_SHOW_CONTENT,
        subaction_name: TrackingConstant.PRICE_COMPARISON_TOOLTIP,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        action_cause: TrackingConstant.ACTION_CAUSE_HOVER,
        creative_id: 'edm-entry-negotiation',
      },
    });
  };

  render() {
    const { vinData, isMobile, isUsedNonPartnerDealCheckAvailable, className } = this.props;
    const sellingSlowly = get(vinData, 'computedDisplayInfo.negotiationTips.sellingSlowly', false);
    const { type, computedDisplayInfo, firstPublishedDate, prices } = vinData;
    const isPartner = isPartnerDealer(vinData);
    const advertisedPrice = get(prices, 'advertisedPrice', 0);
    const totalPriceChange = get(computedDisplayInfo, 'priceHistoryInfo.totalPriceChange', 0);
    const history = get(vinData, 'computedDisplayInfo.priceHistoryInfo.historicalPrices', []);
    const dealerStateCode = get(vinData, 'dealerInfo.address.stateCode', '');
    const concealHistoryPrices = dealerStateCode === HISTORY_PRICES_CONCEALED_STATE;
    const daysListedSince = firstPublishedDate && getDifferenceInDays(firstPublishedDate);
    const isUsedVehicle = isUsed(type);

    const renderDaysSince = (
      <li className="d-flex mb-1 align-items-baseline">
        <i className="icon-pulse2 d-block list-pic pe-0_75" aria-hidden />
        <div>
          {daysListedSince ? (
            <Fragment>
              <span className="fw-bold">{daysListedSince}</span>
              <span>{getPlural(' Day', daysListedSince)} on Edmunds</span>
              {sellingSlowly && (
                <div className="description small">Selling slowly, this dealer is motivated to sell soon</div>
              )}
            </Fragment>
          ) : (
            <span>Days on Edmunds not available</span>
          )}
        </div>
      </li>
    );

    const renderPriceHistory = concealHistoryPrices && (
      <div className="mt-1_5 small">
        Due to Texas Department of Motor Vehicles regulations, Edmunds is unable to display price history for this
        vehicle
      </div>
    );
    const priceValidation = get(vinData, 'computedDisplayInfo.priceValidation');
    const isPriceCheckerEnabled =
      !!advertisedPrice &&
      isUsedVehicle &&
      !!priceValidation &&
      DEAL_TYPE_ALLOWED_VALUES.includes(priceValidation.dealType) &&
      has(priceValidation, 'maxFairPrice') &&
      has(priceValidation, 'maxGoodPrice') &&
      has(priceValidation, 'maxGreatPrice');
    const isUsedDealCheck = isUsedVehicle && isUsedDealCheckAvailable(vinData);

    const showPriceHistoryGraph = !concealHistoryPrices && (isNew(vinData.type) || isPartner);

    return (
      <ContentSection
        className={classnames('negotiation', className)}
        sectionTitle={'Negotiation Insights'}
        dataTrackingParent="edm-entry-negotiation"
        titleClassName="section-title heading-4 mb-1_5"
        line={false}
      >
        <div>
          {isPriceCheckerEnabled && !isUsedDealCheck && !isUsedNonPartnerDealCheckAvailable && (
            <Fragment>
              <div className="mb-1">
                <div className="d-inline-block">Edmunds Price Comparison</div>
                <TooltipItem
                  id={'price-comparison'}
                  item={{
                    placement: 'bottom',
                    text: 'Edmunds Price Comparison evaluates the market value of similar vehicles to determine this vehicle’s market value and rate this deal. This graph represents an approximate distribution of the number of used cars for sale nationally, grouped by price ranges.',
                  }}
                  iconClassName="text-primary-darker"
                  innerClassName="p-1"
                  onTooltipOpen={this.onTooltipOpen}
                />
              </div>
              <RenderWhenViewable>
                <PriceCheckerChartVdp
                  userDealerPrice={advertisedPrice}
                  tmvData={priceValidation}
                  isMobile={isMobile}
                  isPartnerListing={isPartner}
                />
              </RenderWhenViewable>
            </Fragment>
          )}
          <ul className="p-0 m-0">
            {renderDaysSince}
            <InventoryPriceChange concealHistoryPrices={concealHistoryPrices} totalPriceChange={totalPriceChange} />
          </ul>
          {showPriceHistoryGraph && (
            <PriceHistoryGraph
              currentPrice={advertisedPrice}
              history={history}
              firstPublishedDate={firstPublishedDate}
              wrapperClasses="p-0"
            />
          )}
          {renderPriceHistory}

          {!isUsedDealCheck && (
            <InventoryHeart vehicle={this.props.vinData} CustomView={GetPriceDropAlerts} handleNonEmailUser />
          )}
        </div>
      </ContentSection>
    );
  }
}
